import { z } from "zod";
import { UPLOAD_TYPES } from "./api.js";

/** ผลลัพธ์ที่ AI ต้องตอบ (validate ฝั่ง server ทุกครั้ง) และสัญญา API ของ Phase 2 */

export const MATERIAL_GRADES = ["economy", "standard", "premium"] as const;
export type MaterialGrade = (typeof MATERIAL_GRADES)[number];
export const PLANES = ["ceiling", "floor", "wall"] as const;

const unit01 = z.number().min(0).max(1);
/** จุดแบบ normalized 0-1 ตามขนาดภาพ [x, y] */
export const pointSchema = z.tuple([unit01, unit01]);
export const polygonSchema = z.array(pointSchema).min(3).max(12);
export type Polygon = z.infer<typeof polygonSchema>;

export const REJECT_REASONS = ["not_construction", "too_dark", "too_blurry", "no_work_area", "people_or_private", "other"] as const;

export const gateResultSchema = z.object({
  is_construction_site: z.boolean(),
  is_assessable: z.boolean(),
  reject_reason: z.enum(REJECT_REASONS).nullable(),
  suggested_categories: z.array(z.object({ category_id: z.string(), confidence: unit01 })).max(6),
  notes_th: z.string().max(500),
});
export type GateResult = z.infer<typeof gateResultSchema>;

export const aiEstimateSchema = z.object({
  scene: z.object({
    description_th: z.string().max(600),
    reference_objects: z
      .array(
        z.object({
          type: z.enum(["door", "tile", "outlet", "ceiling_grid", "other"]),
          assumed_size_m: z.number().positive().max(10),
          polygon: polygonSchema,
        }),
      )
      .max(10),
  }),
  work_areas: z
    .array(
      z.object({
        category_id: z.string(),
        label_th: z.string().max(120),
        polygon: polygonSchema,
        plane: z.enum(PLANES),
        estimated_dimensions: z.object({
          width_m: z.number().nonnegative().max(500).nullable(),
          length_m: z.number().nonnegative().max(500).nullable(),
          height_m: z.number().nonnegative().max(100).nullable(),
          count: z.number().int().nonnegative().max(10000).nullable(),
        }),
        dimension_confidence: unit01,
      }),
    )
    .min(1)
    .max(8),
  recommended_material_grade: z.enum(MATERIAL_GRADES),
  assumptions_th: z.array(z.string().max(300)).max(12),
  risks_th: z.array(z.string().max(300)).max(12),
  overall_confidence: unit01,
});
export type AiEstimate = z.infer<typeof aiEstimateSchema>;

/** POST /estimates/gate  (ต้องอัปโหลดและ verify รูปก่อน) */
export const gateRequestSchema = z.object({ keys: z.array(z.string().min(1)).min(1).max(3) });
export interface GateResponse {
  gateId: string;
  assessable: boolean;
  reject_reason: GateResult["reject_reason"];
  notes_th: string;
  suggested: { category_id: string; confidence: number }[];
  gateRemainingToday: number;
}

/** POST /estimates -> 202 { estimateId }  ; หัก/จองโควตาที่ขั้นนี้ */
export const startEstimateRequestSchema = z.object({
  gateId: z.string().min(1),
  keys: z.array(z.string().min(1)).min(1).max(3),
  categoryIds: z.array(z.string().min(1)).min(1).max(4),
  hints: z
    .object({
      width_m: z.number().positive().max(500).optional(),
      length_m: z.number().positive().max(500).optional(),
      height_m: z.number().positive().max(100).optional(),
      material_grade: z.enum(MATERIAL_GRADES).optional(),
      note: z.string().max(300).optional(),
    })
    .optional(),
});
export type StartEstimateRequest = z.infer<typeof startEstimateRequestSchema>;

export const ESTIMATE_STATUSES = ["queued", "running", "succeeded", "manual_required", "failed", "cancelled"] as const;
export type EstimateStatus = (typeof ESTIMATE_STATUSES)[number];
export const ESTIMATE_STEPS = ["reading", "measuring", "pricing"] as const;

/** ราคาและแหล่งอ้างอิง ณ วันที่ประเมิน (ไม่เปลี่ยนย้อนหลัง) */
export interface PriceSnapshot {
  category_id: string;
  unit: Category["unit"];
  labor_min_thb: number;
  labor_max_thb: number;
  material_min_thb: number;
  material_max_thb: number;
  price_version: number;
  source: { source_id: string; name: string; kind_label_th: string; url: string; effective_from: string };
}
import type { Category } from "./api.js";

export interface EstimateRecord {
  estimateId: string;
  createdAt: string;
  categoryIds: string[];
  photos: { key: string; url: string }[];
  ai: AiEstimate;
  priceSnapshots: PriceSnapshot[];
  model: { id: string; promptVersion: string };
  hints?: StartEstimateRequest["hints"];
}

/** GET /estimates/{id}  (poll ทุก 1.5 วินาที) */
export interface EstimateStatusResponse {
  estimateId: string;
  status: EstimateStatus;
  step?: (typeof ESTIMATE_STEPS)[number];
  /** สำหรับ manual_required / failed ไม่หักโควตา */
  /** มีเมื่อระบบบันทึกงานอัตโนมัติแล้ว (สถานะ succeeded) */
  jobId?: string;
  reason?: "invalid_ai_output" | "ai_unavailable" | "timeout" | "kill_switch";
  result?: EstimateRecord;
}

/** error 402 จาก API เมื่อชนโควตา */
export interface QuotaErrorBody {
  error: "quota";
  reason: "estimate_limit" | "token_limit" | "gate_limit" | "save_limit" | "quote_locked" | "concurrency" | "disabled";
}

export const IMAGE_TYPES = UPLOAD_TYPES;
